import { Injectable, computed, inject } from '@angular/core'

import { Store } from '@ngrx/store'

import { environment } from '@flowx/designer/environment'
import { HarnessBranchService } from '@flowx/designer/modules/ai-builder/services/harness-branch.service'
import { HarnessTurnContext } from '@flowx/designer/modules/ai-builder/types/conversation.types'
import { isUuid } from '@flowx/designer/modules/ai-builder/utils/text.util'
import { ApplicationPermissionsStore } from '@flowx/designer/modules/projects/store/application-permissions.store'
import { aiStreamHeaders } from '@flowx/designer/shared/api/ai-stream-headers'
import {
  selectAppId,
  selectAppVersionId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

/**
 * ai-gateway's harness relay, behind nginx, which rewrites `/ai/(.*)` to `/$1` upstream — hence
 * the `/ai` in the browser path and not in ai-gateway's own. `http://localhost:9166/harness` with
 * the four `Fx-*` headers supplied by hand reaches the harness directly, which is useful for
 * debugging it in isolation, but authenticates nothing.
 */
export const AI_API_PATH = '/ai/api'
const BASE_PATH = `${AI_API_PATH}/harness`

/**
 * Everything environment-shaped about talking to the harness: the base URL, the headers, and the
 * per-call body fields the harness re-reads on every request. Deployments change the host, not the
 * shape, so this is the only file a new environment should need.
 *
 * **The front-end does not send an identity.** The harness trusts whatever identity header it is
 * handed, so ai-gateway strips `Fx-OriginalUserInfo` and `Fx-Organization-Id` from inbound
 * requests and stamps them from the validated token (a forged `subjectId` was tested and never
 * reached the harness). Adding them here would build toward the hole that hop exists to close.
 */
@Injectable({ providedIn: 'root' })
export class HarnessConfigService {
  #store = inject(Store)
  #branch = inject(HarnessBranchService)
  #permissions = inject(ApplicationPermissionsStore)
  #routeAppVersionId = this.#store.selectSignal(selectAppVersionId)

  workspaceId = this.#store.selectSignal(selectWorkspaceId)
  appId = this.#store.selectSignal(selectAppId)

  /** The version in the route, when it is a UUID; on a project route without one (Runtime), the
   * version the project's Config tab opens, so the conversation works the same on either tab. */
  appVersionId = computed(() => {
    const routeVersionId = this.#routeAppVersionId()
    const appId = this.appId()
    const publishedAppId = this.#branch.appId()
    const publishedVersionId = this.#branch.appVersionId()

    if (routeVersionId) {
      return isUuid(routeVersionId) ? routeVersionId : null
    }

    return appId && publishedAppId === appId ? publishedVersionId : null
  })

  /** Whether there is a harness to talk to: the environment ships the developer agents and the
   * maker may use them — the same rule that shows the sparkles. Where it is off, `/ai` is not
   * routed at all and every call answers 403, so nothing should ask. */
  available = this.#permissions.designerAiAccess

  /** The harness root: the designer's own backend plus the relay's path. Deliberately not an
   * environment key — the path is identical wherever this is deployed, and `baseUrl` already
   * carries the host. */
  baseUrl(): string {
    return `${trimTrailingSlash(environment.baseUrl ?? '')}${BASE_PATH}`
  }

  /** Headers for the two streaming calls, which are the only ones left on `fetch` and so the only
   * ones the interceptor chain never sees. `aiStreamHeaders` is shared with the conversation
   * stream rather than repeated here, so the security-relevant set has one definition. */
  headers(): Record<string, string> {
    return this.headersFor(this.workspaceId(), this.appId())
  }

  /** The same set for a project named explicitly — a conversation's write target, which with
   * conversation-owned plans need not be the project on screen. */
  headersFor(workspaceId: string | null, appId: string | null): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      ...aiStreamHeaders(workspaceId, appId),
    }
  }

  /**
   * Where the maker is standing, as a conversation-owned turn reports it (`context{…}`), or null
   * when the page is not an app version — organization settings, a workspace list — or the
   * version's branch is still loading. Never inferred: a context that names the wrong branch would
   * have the agent read a version the maker is not looking at. The ids are the route's, sent only
   * when they are UUIDs, as the page's own `ui_context` sends them.
   */
  routeContext(): HarnessTurnContext | null {
    const workspaceId = this.workspaceId()
    const appId = this.appId()
    const appVersionId = this.appVersionId()
    const branch = this.branch()

    return workspaceId && isUuid(workspaceId) && appId && isUuid(appId) && appVersionId && branch
      ? { workspace_id: workspaceId, app_id: appId, app_version_id: appVersionId, branch }
      : null
  }

  /**
   * The two fields every harness body repeats. The harness stores nothing between calls, so these
   * are re-sent on each request rather than established once at session open — that is what lets
   * any pod serve any turn.
   *
   * The branch is the real one. A session is scoped to a branch, so the `'main'` this used to send
   * addressed a *different* session on a feature branch: somebody else's interrupt, or a clean rail
   * while the maker's own paused turn sat on the branch in front of them.
   */
  sessionBody(): { app_version_id: string; branch: string } {
    const branch = this.branch()
    if (!branch) {
      throw Object.assign(
        new Error('The application branch is still loading. Try again once it is ready.'),
        {
          code: 'branch_unavailable',
        }
      )
    }
    return { app_version_id: this.appVersionId() ?? '', branch }
  }

  branch(): string {
    return this.#branch.appVersionId() === this.appVersionId() ? this.#branch.branch() : ''
  }
}

function trimTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url
}
